/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { config } from './appConfig';
import { appInsights } from './AppInsights';
import { sessionIdentifier } from "./sessionIdentifier";

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: '7b2773f1-dc5f-4a84-ac82-0855ab6489ac',
        authority: 'https://aderantidentity.b2clogin.com/aderantidentity.onmicrosoft.com/B2C_1A_SIGNUP_SIGNIN',
        redirectUri: "/",
        postLogoutRedirectUri: "/",
        knownAuthorities: ['aderantidentity.b2clogin.com']
    },
    cache: {
        cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        trackEvent(`${sessionIdentifier}-error`, message);
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        trackEvent(`${sessionIdentifier}-info`, message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        trackEvent(`${sessionIdentifier}-debug`, message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        trackEvent(`${sessionIdentifier}-warn`, message);
                        return;
                }
            }
        }
    }
};

const trackEvent = (level, message) => {
    try {
        appInsights.trackEvent({ name: level, message }, { message: message })
    } catch (err) {
        console.log(err);
    }
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: ["https://aderantidentity.onmicrosoft.com/UserManagement/UserManagement.User", "https://aderantidentity.onmicrosoft.com/UserManagement/UserManagement.Admin"]
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
    scopes: ["openid", "profile"],
    loginHint: "example@domain.net"
};